import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import PreviewPhoneFrame from './PreviewPhoneFrame';
import { SHOWCASE_ARTIST_PREVIEW, SHOWCASE_BUSINESS_PREVIEW } from './templatePreviewAssets';
import { TEMPLATES_CHOOSE_URL } from './digitalCardRoutes';

type ShowcasePhoneProps = {
  label: string;
  imageSrc: string;
  imageAlt: string;
  variant: 'business' | 'artist';
  visible: boolean;
  size?: 'sm' | 'md' | 'lg';
};

const ShowcasePhone: React.FC<ShowcasePhoneProps> = ({
  label,
  imageSrc,
  imageAlt,
  variant,
  visible,
  size = 'lg',
}) => {
  const floatRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = floatRef.current;
    if (!el || typeof window === 'undefined') return;
    if (window.matchMedia('(hover: none)').matches) return;

    const handleMove = (e: MouseEvent) => {
      const rect = el.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width - 0.5;
      const y = (e.clientY - rect.top) / rect.height - 0.5;
      el.style.setProperty('--tilt-x', `${y * -4}deg`);
      el.style.setProperty('--tilt-y', `${x * 5}deg`);
    };

    const handleLeave = () => {
      el.style.setProperty('--tilt-x', '0deg');
      el.style.setProperty('--tilt-y', '0deg');
    };

    el.addEventListener('mousemove', handleMove);
    el.addEventListener('mouseleave', handleLeave);
    return () => {
      el.removeEventListener('mousemove', handleMove);
      el.removeEventListener('mouseleave', handleLeave);
    };
  }, []);

  return (
    <div className={`dcl-showcase-phone dcl-showcase-phone--${variant}${visible ? ' dcl-showcase-phone--in' : ''}`}>
      <span className="dcl-showcase-label">{label}</span>
      <div className="dcl-showcase-phone-inner">
        <div ref={floatRef} className="dcl-showcase-phone-float">
          <PreviewPhoneFrame src={imageSrc} alt={imageAlt} size={size} />
        </div>
      </div>
    </div>
  );
};

const TcardsShowcaseSection: React.FC = () => {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -5% 0px' }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className={`dcl-section dcl-section--showcase dcl-section--mesh${visible ? ' dcl-showcase--visible' : ''}`}
      id="showcase"
      aria-labelledby="dcl-showcase-heading"
    >
      <div className="dcl-container">
        <div className={`dcl-showcase-header${visible ? ' dcl-showcase-header--in' : ''}`}>
          <div className="dcl-section-badge">
            <Sparkles size={14} />
            {t('dcl_showcase_badge', { defaultValue: 'Template Preview' })}
          </div>
          <h2 id="dcl-showcase-heading" className="dcl-section-title dcl-showcase-title dcl-section-title--center">
            {t('dcl_showcase_title', { defaultValue: 'One TCard. Your Style. Your Identity.' })}
          </h2>
          <p className="dcl-section-sub dcl-showcase-sub dcl-section-sub--center">
            {t('dcl_showcase_sub', {
              defaultValue:
                "Whether you're building your professional presence or showcasing your creative identity, TCards gives you a beautiful digital card designed around you.",
            })}
          </p>
        </div>

        <div className="dcl-showcase-stage">
          <ShowcasePhone
            variant="business"
            visible={visible}
            size="lg"
            label={t('dcl_showcase_business_label', { defaultValue: 'Business & Professional Profiles' })}
            imageSrc={SHOWCASE_BUSINESS_PREVIEW}
            imageAlt={t('dcl_showcase_business_alt', { defaultValue: 'TCards Business digital card example' })}
          />
          <ShowcasePhone
            variant="artist"
            visible={visible}
            size="lg"
            label={t('dcl_showcase_artist_label', { defaultValue: 'Artists & Creative Profiles' })}
            imageSrc={SHOWCASE_ARTIST_PREVIEW}
            imageAlt={t('dcl_showcase_artist_alt', { defaultValue: 'TCards Artist digital card example' })}
          />
        </div>

        <div className={`dcl-showcase-cta${visible ? ' dcl-showcase-cta--in' : ''}`}>
          <Link to={TEMPLATES_CHOOSE_URL} className="dcl-btn dcl-btn--primary dcl-btn--pill">
            {t('dcl_cta_explore_templates', { defaultValue: 'Explore Templates' })}
          </Link>
        </div>
      </div>
    </section>
  );
};

export default TcardsShowcaseSection;
